import { z } from 'zod';
/**
 * Reporting agent — generative-UI tool payloads.
 *
 * Every render-tool the agent can call has a Zod payload schema below.
 * The frontend's ToolCallRenderer switches on the tool name and renders
 * the matching component, validating the payload against these schemas
 * at runtime. The backend constructs payloads to match.
 *
 * Adding a new render-tool: add a payload schema, add it to the
 * ToolCallSchema discriminated union, add a literal to REPORTING_TOOL_NAMES.
 */
// ─── Clarification card ───────────────────────────────────────────────────
export const ClarificationOptionSchema = z.object({
    id: z.string(),
    label: z.string(),
    default: z.boolean().optional(),
});
export const ClarificationCardPayloadSchema = z.object({
    title: z.string(),
    helpText: z.string().optional(),
    fieldKey: z
        .string()
        .describe('Identifier the agent uses to map the user response back to a parameter slot.'),
    options: z.array(ClarificationOptionSchema).min(1),
    multiSelect: z.boolean().default(false),
});
// ─── Report preview (confirm before run) ──────────────────────────────────
export const ReportPreviewPayloadSchema = z.object({
    templateId: z.string(),
    version: z.number().int().positive(),
    summary: z
        .string()
        .describe('One-line human-readable description of the planned report.'),
    params: z.record(z.unknown()),
    estimatedRows: z.number().int().nonnegative().optional(),
    estimatedSeconds: z.number().nonnegative().optional(),
});
// ─── Progress (long-running runs) ─────────────────────────────────────────
export const ProgressPayloadSchema = z.object({
    step: z.number().int().nonnegative(),
    total: z.number().int().positive(),
    label: z.string(),
});
// ─── Result table ─────────────────────────────────────────────────────────
export const TableColumnSchema = z.object({
    key: z.string(),
    label: z.string(),
    group: z
        .string()
        .optional()
        .describe('Optional grouped-header label (e.g. "April 2026" for a pivot).'),
    align: z.enum(['left', 'center', 'right']).optional(),
    format: z
        .enum(['number', 'currency', 'percent', 'date', 'string'])
        .optional(),
});
/**
 * A dimension the viewer can slice the rendered table by. The dropdown's
 * options are derived on the frontend from the distinct values present in
 * `rows`, so the backend only names the column — it never has to ship the
 * option list, and the options can't drift from the data.
 */
export const TableFilterSchema = z.object({
    key: z
        .string()
        .describe('Column key to filter on. Must match a key in `columns`.'),
    label: z
        .string()
        .describe('Dropdown label shown above the table (e.g. "State").'),
    mode: z
        .enum(['multi', 'single'])
        .optional()
        .describe('multi (checkboxes, default) or single (radios).'),
});
/**
 * Optional metadata about how the report was generated. Set by the
 * freeform path (Slice 8.1) so the FE can render the "Dynamic" badge
 * and the "Show recipe" affordance. Absent for curated reports.
 *
 * Slice 8.6 extends the recipe with an optional `secondary` + `join`
 * for chained reports (two service-method calls combined server-side).
 */
const recipeMethodSchema = z.object({
    serviceName: z.string(),
    methodName: z.string(),
    params: z.record(z.unknown()),
    capabilities: z.record(z.unknown()).optional(),
});
/**
 * A report built from several named datasets and one query over them. When
 * present, `datasets` + `sql` are what runs; the top-level method mirrors the
 * first dataset.
 */
const recipeDatasetsSchema = z.array(recipeMethodSchema.extend({ name: z.string() }));
export const ReportMetaSchema = z.object({
    isDynamic: z
        .boolean()
        .optional()
        .describe('True when this report was generated by the freeform path (no curated template fit).'),
    recipe: recipeMethodSchema
        .extend({
        secondary: recipeMethodSchema
            .optional()
            .describe('Present when this report was a chained 2-method join. The secondary orchestration spec.'),
        join: z
            .object({
            leftKey: z.string(),
            rightKey: z.string(),
            type: z.enum(['inner', 'left']),
        })
            .optional()
            .describe('Present when this report was chained. Join clause connecting primary and secondary rows.'),
        datasets: recipeDatasetsSchema.optional().describe('Present when the report was built by a query over named datasets.'),
        sql: z.string().optional().describe('The query that built the report from `datasets`.'),
        summary: z
            .string()
            .optional()
            .describe('The recipe in plain words, for the "Show recipe" line.'),
    })
        .optional()
        .describe('The orchestration spec used to generate this report. Surfaced in the "Show recipe" UI affordance so users can audit what was called.'),
});
export const ResultTablePayloadSchema = z.object({
    title: z.string().optional(),
    columns: z.array(TableColumnSchema).min(1),
    rows: z.array(z.record(z.unknown())),
    aggregateRow: z
        .record(z.unknown())
        .optional()
        .describe('Optional totals row keyed by column key — rendered at table bottom.'),
    filters: z
        .array(TableFilterSchema)
        .optional()
        .describe('Dimensions the viewer can slice this table by, rendered as dropdowns above it.'),
    /**
     * Relative path or absolute URL. Backend emits relative paths
     * (e.g. /reporting/artifacts/...) which the FE resolves against the
     * API base. We don't use z.string().url() because it rejects paths.
     */
    downloadUrl: z.string().min(1).optional(),
    meta: ReportMetaSchema.optional(),
});
// ─── Result chart ─────────────────────────────────────────────────────────
export const ChartSeriesSchema = z.object({
    key: z.string(),
    label: z.string(),
    color: z.string().optional(),
    renderAs: z.enum(['line', 'bar', 'area']).optional(),
});
export const ResultChartPayloadSchema = z.object({
    kind: z.enum(['line', 'bar', 'area', 'mixed', 'pie']),
    title: z.string().optional(),
    subtitle: z.string().optional(),
    data: z.array(z.record(z.unknown())),
    dimensionKey: z.string(),
    series: z.array(ChartSeriesSchema).min(1),
});
// ─── KPI scorecard ────────────────────────────────────────────────────────
export const KpiSchema = z.object({
    label: z.string(),
    value: z.number(),
    unit: z.string().optional(),
    deltaPct: z.number().optional(),
    deltaLabel: z
        .string()
        .optional()
        .describe('e.g. "vs last month" — what the delta is compared against.'),
    direction: z.enum(['up', 'down', 'flat']).optional(),
});
export const KpiScorecardPayloadSchema = z.object({
    title: z.string().optional(),
    kpis: z.array(KpiSchema).min(1),
});
// ─── Download artifact ────────────────────────────────────────────────────
export const ArtifactFormatSchema = z.enum(['csv', 'xlsx']);
export const ArtifactAlternateSchema = z.object({
    format: ArtifactFormatSchema,
    /** Relative path or absolute URL — same convention as the primary url. */
    url: z.string().min(1),
    filename: z.string(),
    sizeBytes: z.number().int().positive().optional(),
});
export const DownloadArtifactPayloadSchema = z.object({
    /**
     * Relative path or absolute URL. Backend emits relative paths
     * (e.g. /reporting/artifacts/...) which the FE resolves against the
     * API base. We don't use z.string().url() because it rejects paths.
     */
    url: z.string().min(1),
    filename: z.string(),
    expiresAt: z.string().datetime(),
    sizeBytes: z.number().int().positive().optional(),
    /**
     * Format of the primary artifact at `url`. Defaults to 'csv' when
     * the backend doesn't set it (back-compat with pre-XLSX payloads).
     */
    format: ArtifactFormatSchema.optional(),
    /**
     * Same data in additional formats — rendered as small inline buttons
     * next to the primary download. Used to ship CSV + XLSX from one card
     * without making the chat feel noisy.
     */
    alternates: z.array(ArtifactAlternateSchema).optional(),
});
// ─── Offer to save the report ─────────────────────────────────────────────
/**
 * Polymorphic save offer — supports curated and freeform reports.
 *
 * recipeKind is the discriminator. Slice 8.2 added the 'freeform'
 * variant; older clients that don't read recipeKind keep working
 * because template was the only kind and remains the default.
 */
const OfferSaveTemplatePayloadSchema = z.object({
    recipeKind: z.literal('template').optional(),
    templateId: z.string(),
    version: z.number().int().positive(),
    params: z.record(z.unknown()),
    suggestedName: z.string().optional(),
});
const OfferSaveFreeformPayloadSchema = z.object({
    recipeKind: z.literal('freeform'),
    freeformSpec: z.object({
        serviceName: z.string(),
        methodName: z.string(),
        params: z.record(z.unknown()),
        capabilities: z.record(z.unknown()).optional(),
        title: z.string(),
        secondary: z
            .object({
            serviceName: z.string(),
            methodName: z.string(),
            params: z.record(z.unknown()),
            capabilities: z.record(z.unknown()).optional(),
        })
            .optional(),
        join: z
            .object({
            leftKey: z.string(),
            rightKey: z.string(),
            type: z.enum(['inner', 'left']),
        })
            .optional(),
        shape: z.enum(['table', 'kpi', 'chart']).optional(),
        kpis: z
            .array(z.object({
            summaryKey: z.string(),
            label: z.string(),
            unit: z.string().optional(),
        }))
            .optional(),
        chartConfig: z
            .object({
            xField: z.string(),
            yField: z.string(),
            seriesField: z.string().optional(),
            chartType: z.enum(['line', 'bar']),
        })
            .optional(),
        // Undeclared keys are stripped when the client parses this payload, and
        // the client is what posts it back on save — so anything the re-run
        // needs has to be declared here or it is silently lost.
        filterColumns: z.array(z.string()).optional(),
        fullReport: z.boolean().optional(),
        datasets: recipeDatasetsSchema.optional(),
        sql: z.string().optional(),
    }),
    suggestedName: z.string().optional(),
});
export const OfferSaveReportPayloadSchema = z.union([
    OfferSaveTemplatePayloadSchema,
    OfferSaveFreeformPayloadSchema,
]);
// ─── Discriminated union of all tool calls ────────────────────────────────
export const REPORTING_TOOL_NAMES = [
    'showClarificationCard',
    'showReportPreview',
    'showProgress',
    'showResultTable',
    'showResultChart',
    'showKpiScorecard',
    'showDownloadArtifact',
    'offerSaveReport',
];
export const ToolCallSchema = z.discriminatedUnion('name', [
    z.object({
        name: z.literal('showClarificationCard'),
        payload: ClarificationCardPayloadSchema,
    }),
    z.object({
        name: z.literal('showReportPreview'),
        payload: ReportPreviewPayloadSchema,
    }),
    z.object({
        name: z.literal('showProgress'),
        payload: ProgressPayloadSchema,
    }),
    z.object({
        name: z.literal('showResultTable'),
        payload: ResultTablePayloadSchema,
    }),
    z.object({
        name: z.literal('showResultChart'),
        payload: ResultChartPayloadSchema,
    }),
    z.object({
        name: z.literal('showKpiScorecard'),
        payload: KpiScorecardPayloadSchema,
    }),
    z.object({
        name: z.literal('showDownloadArtifact'),
        payload: DownloadArtifactPayloadSchema,
    }),
    z.object({
        name: z.literal('offerSaveReport'),
        payload: OfferSaveReportPayloadSchema,
    }),
]);
//# sourceMappingURL=tool-payloads.js.map